import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  DialogContent,
  Button,
  TextField,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  Tooltip,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';

import { ModalActions } from '@/RestoreComponent/ModalActions';
import CardLoader from '@/components/CardLoader';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    '& .MuiPaper-root': {
      width: 472
    },
    '& label': {
      margin: '8px 0'
    }
  },
  title: {
    fontSize: 12,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 8px 0 24px',
    '& span': {
      fontSize: 18,
      marginTop: 12
    }
  },
  content: {
    paddingBottom: 6
  },
  actionMessage: {
    fontSize: 16,
    fontWeight: 500,
    margin: '8px 0'
  }
}));

const testValidEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

interface EmailReceiptModalProps {
  open: boolean;
  onClose: () => void;
  receiptId: number;
  customerEmail: string;
  customerName: string;
}

export const EmailReceiptModal: React.FC<EmailReceiptModalProps> = ({
  open,
  onClose = () => void 0,
  receiptId,
  customerEmail,
  customerName
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(customerEmail);
  const [emailOption, setEmailOption] = useState<number>(0);
  const [emailIsValid, setEmailIsValid] = useState<boolean>(
    testValidEmail(customerEmail)
  );

  useEffect(() => {
    setEmailIsValid(testValidEmail(email));
  }, [email]);

  async function emailReceipt() {
    setLoading(true);
    formDataInstance
      .post(
        `${CONFIG.API}/pos_retail2/email_purchase_receipt/`,
        { purchaseId: receiptId, email },
        { headers: getRestHeaders() }
      )
      .then(() => {
        enqueueSnackbar(`The receipt has been emailed to ${email}`, {
          variant: 'success'
        });
        onClose();
      })
      .catch(() =>
        enqueueSnackbar('Receipt copy failed to send.', {
          variant: 'error'
        })
      )
      .finally(() => setLoading(false));
  }

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason && reason === 'backdropClick') {
          return;
        }
        onClose();
      }}
      className={classes.root}
    >
      <DialogTitle variant="h6" component="div" className={classes.title}>
        <span>Email Receipt</span>
        <Tooltip arrow title="Close">
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 11 }}>
            <CardLoader />
          </Box>
        )}
        {!loading && (
          <Grid container mt={1}>
            <Grid item xs={12}>
              <Typography className={classes.actionMessage}>
                Choose a recipient.
              </Typography>
              <RadioGroup
                aria-label="Email Receipt To"
                name="controlled-radio-buttons-group"
                value={emailOption}
                onChange={e => {
                  setEmailOption(e.target.value as unknown as number);
                  if (e.target.value === '0') {
                    setEmail(customerEmail);
                  } else {
                    setEmail('');
                  }
                }}
              >
                <FormControlLabel
                  value={0}
                  control={<Radio sx={{ p: 0.5, m: 0, ml: 1 }} />}
                  label={
                    <>
                      <Typography variant="body1">{customerName}</Typography>
                      <Typography variant="body4">{customerEmail}</Typography>
                    </>
                  }
                />
                <FormControlLabel
                  value={1}
                  control={<Radio sx={{ p: 0.5, m: 0, ml: 1 }} />}
                  label={<Typography variant="body1">Other</Typography>}
                />
              </RadioGroup>
              <TextField
                fullWidth
                id="elsewhere"
                key="elsewhere"
                size="medium"
                placeholder="Enter email"
                sx={{ ml: 5, mt: 1, width: 384 }}
                disabled={emailOption == 0}
                value={emailOption == 1 ? email : ''}
                onChange={e => {
                  setEmail(e.target.value);
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <ModalActions>
        <Button color="primary" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          type="button"
          loading={loading}
          color="primary"
          variant="contained"
          onClick={emailReceipt}
          disabled={loading || !emailIsValid}
        >
          Send Email
        </LoadingButton>
      </ModalActions>
    </Dialog>
  );
};

const DefaultErrorMessageComponent: React.FC<{ errors: string[] }> = ({
  errors
}) => {
  return <>{errors[0]}</>;
};

const DefaultDescriptionComponent: React.FC = () => {
  return null;
};
